import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { io } from 'socket.io-client';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = API_URL
  ? API_URL.replace(/\/api\/v1\/?$/, '')
  : (window.location.port === '3001' ? 'http://localhost:3000' : window.location.origin);

const CrmBookingsCalendar = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', isNewBooking: false });

  const fetchBookings = useCallback(async () => {
    try {
      setError('');
      const url = `${API_URL || '/api/v1'}/bookings`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success && data.data) {
        setBookings(data.data);
      } else {
        setBookings([]);
        setError(data.error || 'Error al cargar reservas');
      }
    } catch (e) {
      setBookings([]);
      setError('No se pudo conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('crm_token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [navigate, fetchBookings]);

  useEffect(() => {
    const socket = io(API_BASE, { path: '/socket.io', transports: ['websocket', 'polling'] });
    socket.on('newBooking', (payload) => {
      setSnack({
        open: true,
        message: `Nueva reserva: ${payload.email} - ${payload.slot_label}`,
        isNewBooking: true,
      });
      setBookings((prev) => [{ ...payload, created_at: payload.created_at || new Date().toISOString() }, ...prev]);
    });
    return () => socket.disconnect();
  }, [API_BASE]);

  const handleMarkCitaEnviada = async (id) => {
    setSendingId(id);
    try {
      const res = await fetch(`${API_URL || '/api/v1'}/bookings/${id}/cita-enviada`, {
        method: 'PATCH',
      });
      const data = await res.json();
      if (data.success) {
        setBookings((prev) =>
          prev.map((b) => (b.id === id ? { ...b, cita_enviada: 1 } : b))
        );
      } else {
        setSnack({ open: true, message: data.error || 'Error', isNewBooking: false });
      }
    } catch (e) {
      setSnack({ open: true, message: 'Error de conexión', isNewBooking: false });
    } finally {
      setSendingId(null);
    }
  };

  const handleCloseSnack = () => setSnack((s) => ({ ...s, open: false }));

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <>
      <Helmet>
        <title>Calendario de reservas - PeopleForTech CRM</title>
      </Helmet>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <CalendarMonthIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight={700}>
            Calendario de reservas
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/crm/dashboard')}>
            Volver al dashboard
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Fecha / Hora</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Slot</strong></TableCell>
                  <TableCell><strong>Precio</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell><strong>Cita enviada</strong></TableCell>
                  <TableCell align="right"><strong>Acción</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No hay reservas</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  bookings.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell>{formatDate(b.created_at)}</TableCell>
                      <TableCell>{b.email}</TableCell>
                      <TableCell>{b.slot_label}</TableCell>
                      <TableCell>{b.price === 0 ? 'Gratis' : `${b.price}€`}</TableCell>
                      <TableCell>
                        <Chip
                          label={b.status === 'confirmed' ? 'Confirmada' : 'Pendiente'}
                          color={b.status === 'confirmed' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {b.cita_enviada ? (
                          <Chip label="Sí" color="success" size="small" />
                        ) : (
                          <Chip label="No" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!b.cita_enviada && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SendIcon />}
                            disabled={sendingId === b.id}
                            onClick={() => handleMarkCitaEnviada(b.id)}
                            sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}
                          >
                            {sendingId === b.id ? '...' : 'Cita enviada'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Snackbar
          open={snack.open}
          autoHideDuration={snack.isNewBooking ? 6000 : 4000}
          onClose={handleCloseSnack}
          message={snack.message}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={snack.isNewBooking ? { '& .MuiSnackbar-content': { backgroundColor: '#2e7d32', color: '#fff' } } : {}}
          action={
            <IconButton size="small" color="inherit" onClick={handleCloseSnack}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Container>
    </>
  );
};

export default CrmBookingsCalendar;
